// src/pages/screens/VMScreen.js
import React, { useEffect, useState } from "react";
import { useDatabase } from "../databaseContext";
import { format } from "date-fns";
import { Button } from "antd";
const VMScreen = ({ showNotification, onTabChange }) => {
  const { dbEndpoints, fetchDbEndpoints, setDbEndpoints } = useDatabase();
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchHost, setSearchHost] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadEndpoints();
  }, [selectedStatus, searchHost, currentPage]);

  const loadEndpoints = async () => {
    setLoading(true);
    const filters = [];
    if (searchHost) {
      filters.push({ filterParameter: "host", filterValue: searchHost });
    }
    if (selectedStatus) {
      filters.push({ filterParameter: "status", filterValue: selectedStatus });
    }
    const { data, totalCount: tc } = await fetchDbEndpoints(filters, pageSize, currentPage + 1);
    setDbEndpoints(data);
    setTotalCount(tc);
    setLoading(false);
  };

  const handleViewDatabases = (host) => {
    // Pass the host filter when changing to databases tab
    onTabChange("databases", { hostFilter: host });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Hosts</h2>
        <Button type="primary" onClick={loadEndpoints}>
          Refresh Endpoints
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
            <input
              type="text"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md"
              placeholder="Search by host"
              value={searchHost}
              onChange={(e) => {
                setSearchHost(e.target.value);
                setCurrentPage(0);
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(0);
              }}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button type="primary" onClick={loadEndpoints}>
              Apply Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Endpoints List */}
      {loading ? (
        <div className="py-12 flex justify-center">Loading...</div>
      ) : dbEndpoints.length === 0 ? (
        <div className="py-12 flex flex-col items-center justify-center text-gray-500">
          <p>No endpoints found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {dbEndpoints.map((ep) => (
            <div key={ep.host} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{ep.host}</h3>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        ep.status === "Active"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {ep.status}
                    </span>
                    <Button type="primary" onClick={() => handleViewDatabases(ep.host)}>
                      View Databases
                    </Button>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Created At</h4>
                    {/* The endpoint's created_at is a Unix timestamp in your example */}
                    <p className="mt-1 text-sm text-gray-900">
                      {format(new Date(ep.created_at * 1000), "yyyy-MM-dd HH:mm:ss")}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Database Count</h4>
                    <p className="mt-1 text-sm text-gray-900">{ep.database_count}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Basic Pagination UI */}
      {dbEndpoints.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
          <div className="text-sm text-gray-700">
            Showing {currentPage * pageSize + 1} to{" "}
            {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount} results
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className={`px-2 py-2 rounded-l-md border ${
                  currentPage === 0
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={(currentPage + 1) * pageSize >= totalCount}
                className={`px-2 py-2 rounded-r-md border ${
                  (currentPage + 1) * pageSize >= totalCount
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default VMScreen;
